import React from "react";
import PropTypes from "prop-types";
import {
  settingsModal,
  settingsRadio,
  settingsTitle,
  settingsWrapper,
} from "./settings.module.css";

const Settings = ({
  visible,
  callback,
  settings,
  settingsCallback,
  variant = "modal",
  featureFlags,
}) => {
  if (variant === "accordion") {
    return (
      <SettingsInner
        settings={settings}
        settingsCallback={settingsCallback}
        featureFlags={featureFlags}
        accordion
      />
    );
  }

  return (
    <div>
      {visible && (
        <div
          onClick={() => callback(false)}
          className={settingsWrapper}
          style={{ opacity: visible ? 1 : 0, transition: "opacity 0.3s ease" }}
        >
          <SettingsInner
            settings={settings}
            settingsCallback={settingsCallback}
            featureFlags={featureFlags}
          />
        </div>
      )}
    </div>
  );
};

const SettingsInner = ({ settings, settingsCallback, featureFlags, accordion }) => {
  const handleChange = (data, key) => {
    const newSettings = JSON.parse(JSON.stringify(settings));
    newSettings[key] = data;
    settingsCallback(newSettings);
  };

  const leaveEnabled = featureFlags?.leave !== false;
  const equipmentEnabled = featureFlags?.equipment === true;

  return (
    <div
      className={accordion ? undefined : settingsModal}
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={
        accordion
          ? {
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 4,
              padding: 16,
              marginTop: 8,
              marginBottom: 12,
            }
          : undefined
      }
      data-cy="timesheet-format-settings"
    >
      <h4 className={settingsTitle} style={accordion ? { fontSize: 18, marginBottom: 12 } : undefined}>
        Format Settings
      </h4>

      <div>
        <p>Time format</p>

        <ul className={settingsRadio}>
          <li>
            <input
              type="radio"
              checked={settings.timeFormat == "dec"}
              onChange={() => handleChange("dec", "timeFormat")}
            />
            <label> Decimal (32.93)</label>
          </li>
          <li>
            <input
              type="radio"
              checked={settings.timeFormat == "hm"}
              onChange={() => handleChange("hm", "timeFormat")}
            />
            <label> Hour + min (32 h 55.8 mins)</label>
          </li>
        </ul>
      </div>

      <div>
        <p>Column Settings</p>

        <ul className={settingsRadio}>
          <li>
            <input
              type="checkbox"
              checked={settings.groupByDay === true}
              onChange={(e) => handleChange(e.target.checked, "groupByDay")}
            />
            <label> Group entries by day (show daily totals)</label>
          </li>
          <li>
            <input
              type="checkbox"
              checked={settings.showNotes !== false}
              onChange={(e) => handleChange(e.target.checked, "showNotes")}
            />
            <label> Show Notes</label>
          </li>
          <li>
            <input
              type="checkbox"
              checked={settings.showStaffType}
              onChange={(e) => handleChange(e.target.checked, "showStaffType")}
            />
            <label> Show Staff Type</label>
          </li>
          <li>
            <input
              type="checkbox"
              checked={settings.showTaskType}
              onChange={(e) => handleChange(e.target.checked, "showTaskType")}
            />
            <label> Show Work Type</label>
          </li>
          {equipmentEnabled && (
            <li>
              <input
                type="checkbox"
                checked={settings.showEquipment}
                onChange={(e) => handleChange(e.target.checked, "showEquipment")}
              />
              <label> Show Equipment</label>
            </li>
          )}
          <li>
            <input
              type="checkbox"
              checked={settings.showGeolocation}
              onChange={(e) => handleChange(e.target.checked, "showGeolocation")}
            />
            <label> Show Geolocation link</label>
          </li>
          <li>
            <input
              type="checkbox"
              checked={settings.showGrossHours !== false}
              onChange={(e) => handleChange(e.target.checked, "showGrossHours")}
            />
            <label> Show Gross Hours</label>
          </li>
          <li>
            <input
              type="checkbox"
              checked={settings.showLunch !== false}
              onChange={(e) => handleChange(e.target.checked, "showLunch")}
            />
            <label> Show Breaks</label>
          </li>
          <li>
            <input
              type="checkbox"
              checked={settings.showNetHours !== false}
              onChange={(e) => handleChange(e.target.checked, "showNetHours")}
            />
            <label> Show Net Hours</label>
          </li>
          <li>
            <input
              type="checkbox"
              checked={settings.roundingEnabled == true}
              onChange={(e) => handleChange(e.target.checked, "roundingEnabled")}
            />
            <label> Show rounded hours column (nearest 15 min)</label>
          </li>
        </ul>
      </div>

      <div>
        <p>Staff Order</p>

        <ul className={settingsRadio}>
          <li>
            <input
              type="radio"
              checked={settings.staffOrder == "firstname"}
              onChange={() => handleChange("firstname", "staffOrder")}
            />
            <label> First Name</label>
          </li>
          <li>
            <input
              type="radio"
              checked={settings.staffOrder == "lastname"}
              onChange={() => handleChange("lastname", "staffOrder")}
            />
            <label> Last Name</label>
          </li>
        </ul>
      </div>

      {leaveEnabled && (
        <div>
          <p>Leave</p>

          <ul className={settingsRadio}>
            <li>
              <input
                type="radio"
                checked={settings.showLeave == true}
                onChange={() => handleChange(true, "showLeave")}
              />
              <label> Include Leave hours</label>
            </li>
            <li>
              <input
                type="radio"
                checked={settings.showLeave == false}
                onChange={() => handleChange(false, "showLeave")}
              />
              <label> Ignore Leave hours</label>
            </li>
          </ul>
        </div>
      )}

      {featureFlags?.timesheetApprovals === true && (
        <div>
          <p>Approvals</p>

          <ul className={settingsRadio}>
            <li>
              <input
                type="checkbox"
                checked={settings.approvedOnly === true}
                onChange={(e) => handleChange(e.target.checked, "approvedOnly")}
              />
              <label> Show approved timesheets only</label>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

Settings.propTypes = {
  visible: PropTypes.bool,
  callback: PropTypes.func,
  settings: PropTypes.object.isRequired,
  settingsCallback: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["modal", "accordion"]),
  featureFlags: PropTypes.object,
};

SettingsInner.propTypes = {
  settings: PropTypes.object.isRequired,
  settingsCallback: PropTypes.func.isRequired,
  featureFlags: PropTypes.object,
  accordion: PropTypes.bool,
};

export default Settings;
